import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { faBan, faEnvelope, faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { BsModalService } from 'ngx-bootstrap/modal';
import { interval, Observable, Subject } from 'rxjs';
import { takeUntil, tap } from 'rxjs/operators';
import { ConfigConstants } from '../../config/config-constants';
import { NOTIFICATION_STATUS_CODE } from '../../enums/notification-status-code.enum';
import { ClientManagerInstanceToken, ClientManagerInterface } from '../../interfaces/client-manager.interface';
import { NotificationUpdateModel } from '../../models/low-level/notification-update.model';
import { NotificationModel } from '../../models/low-level/notification.model';
import { EventService } from '../../services/event/event.service';
import { NotificationDataInstanceToken, NotificationDataInterface } from '../../interfaces/notification-data.interface';
import { NotificationUpdateService } from '../../services/notification-update/notification-update.service';
import { RouterHelperService } from '../../services/router-helper/router-helper.service';
import { BaseCancellationComponent } from '../base-cancellation/base-cancellation.component';
import {
    CancellationComponentInstanceToken,
    CancellationComponentInterface,
} from '../cancellation/cancellation-component.instance';
import { CancellationModel } from '../cancellation/cancellation.model';
import { UserMasterInstanceToken, UserMasterInterface } from '../../interfaces/user-master.interface';

@Component({
    selector: 'pace-core-whats-new',
    templateUrl: './whats-new.component.html',
    styleUrls: ['./whats-new.component.scss'],
    standalone: false,
})
export class WhatsNewComponent implements OnInit, OnDestroy {
    public newUpdates$: Observable<NotificationUpdateModel[]>;

    public faBan = faBan;
    public faInfoCircle = faInfoCircle;
    public faEnvelope = faEnvelope;
    public displayNotificationNumber: boolean;
    private destroy$ = new Subject<void>();
    public constructor(
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        private readonly modalService: BsModalService,
        private readonly router: Router,
        private readonly eventService: EventService,
        private readonly notificationUpdateService: NotificationUpdateService,
        @Inject(NotificationDataInstanceToken) private readonly notificationService: NotificationDataInterface,
        private readonly routerHelper: RouterHelperService,
        @Inject(UserMasterInstanceToken) private readonly userMasterService: UserMasterInterface,
        @Inject(ClientManagerInstanceToken) private readonly clientManager: ClientManagerInterface,
        @Inject(CancellationComponentInstanceToken) private readonly cancellationComponent: BaseCancellationComponent,
    ) {
        this.displayNotificationNumber = true;
    }

    public ngOnInit(): void {
        this.newUpdates$ = this.notificationUpdateService.newUpdatesSyncCompleted$;

        // For PACEOnline we are triggering the triggerNewUpdatesSyncCompleted event in a twelve minute interval(5 times per hour).
        // This event is typically triggered from Apple Push Notification events to receive WhatsNew changes and
        // finally, if required, the latest notification data. PACEOnline does not have Apple Push Notifications
        // therefore we are faking the behavior in order to check for WhatsNew in a regular interval.
        // The event is used because it includes all necessary steps in all other services and components to be executed
        // The event will cause the init function of NotificationUpdateService to be called which loads WhatsNew data.
        if (this.userMasterService.isPaceOnlineUser()) {
            const TWELVE_MINUTES = 720000;
            interval(TWELVE_MINUTES)
                .pipe(
                    tap(() => this.eventService.triggerNewUpdatesRequired()),
                    takeUntil(this.destroy$),
                )
                .subscribe();
        }
    }

    public ngOnDestroy(): void {
        this.destroy$.next();
    }

    public async onUpdateClick(update: NotificationUpdateModel): Promise<void> {
        const notificationModel = this.notificationService.getNotificationByIdentifier(update.notificationId);
        if (notificationModel) {
            switch (update.messageType) {
                case NOTIFICATION_STATUS_CODE.AVR1:
                case NOTIFICATION_STATUS_CODE.AVR2:
                    await this.openCancellation(notificationModel, update.notificationUpdateKey);
                    break;
                case NOTIFICATION_STATUS_CODE.ADA:
                case NOTIFICATION_STATUS_CODE.REFR:
                    if (this.notificationService.getStatus(notificationModel).isConfirmedOrCancellationConfirmed()) {
                        this.log.debug(
                            `whatsnew update for notification ${update.notificationId.toString()} with status ${
                                this.notificationService.getStatus(notificationModel).statusCode
                            } cannot route to details screen`,
                        );
                    } else {
                        this.saveWorkAndRouteToDetails(notificationModel);
                    }
                    this.notificationUpdateService.removeUpdate(update.notificationUpdateKey);
                    break;
            }
        } else {
            // remove the update if notification was not found
            this.notificationUpdateService.removeUpdate(update.notificationUpdateKey);
        }
    }

    private saveWorkAndRouteToDetails(notification: NotificationModel): void {
        this.router
            .navigate([
                ConfigConstants.routeUrlDetails,
                notification.identifier.getFrontId(),
                notification.identifier.getQmnum(),
                notification.identifier.getManum(),
            ])
            .catch((error) =>
                this.log.error(
                    'Error in WhatsNewComponent -> saveWorkAndRouteToDetails -> router.navigate : ',
                    JSON.stringify(error),
                ),
            );
    }

    private async openCancellation(notification: NotificationModel, notificationUpdateKey: string): Promise<void> {
        const cancellationModel = await CancellationModel.create(notification, notificationUpdateKey);
        const cancellationComponent = this.cancellationComponent as unknown as new (
            ...args: unknown[]
        ) => BaseCancellationComponent;
        const modalInstance = this.modalService.show(cancellationComponent, {
            class: 'modal-md',
            initialState: {
                model: cancellationModel,
            },
            ariaLabelledBy: 'cancellation-modal-title',
        });

        (<CancellationComponentInterface>modalInstance.content).modalResult.subscribe(
            (result: { notificationUpdateKey: string }) => {
                if (result && result.notificationUpdateKey) {
                    this.notificationUpdateService.removeUpdate(result.notificationUpdateKey);
                }

                const isDetailsUrl = this.router.url.indexOf(ConfigConstants.routeUrlDetails) > -1;
                // Go back to Dashboard if currently user is at notification-details screen of a cancelled
                // (not in-progress) notification, to prevent further actions
                if (
                    isDetailsUrl &&
                    this.routerHelper.params.frontid === notification.identifier.getFrontId() &&
                    this.routerHelper.params.qmnum === notification.identifier.getQmnum() &&
                    this.routerHelper.params.manum === notification.identifier.getManum() &&
                    !this.notificationService.isNotificationInProgress(notification.identifier)
                ) {
                    this.router
                        .navigate([ConfigConstants.routeUrlDashboard])
                        .catch((error: Error) =>
                            this.log.error(
                                'Error in WhatsNewComponent -> openCancellation -> router.navigate : ',
                                JSON.stringify(error),
                            ),
                        );
                } else if (
                    !isDetailsUrl &&
                    this.notificationService.isNotificationInProgress(notification.identifier)
                ) {
                    // Route to details screen so that user can send confirmation of in-progress notification
                    this.saveWorkAndRouteToDetails(notification);
                }
            },
        );
    }

    public isUpdateTypeLink(update: NotificationUpdateModel): boolean {
        const notificationModel = this.notificationService.getNotificationByIdentifier(update.notificationId);
        return !!(
            notificationModel &&
            (update.messageType === NOTIFICATION_STATUS_CODE.ADA ||
                update.messageType === NOTIFICATION_STATUS_CODE.REFR)
        );
    }

    public getCancelledUpdatesCount(): number {
        return this.notificationUpdateService.getCancelledUpdatesCount();
    }
}
